import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, X, Send, Trash2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEventChatbot } from '@/hooks/useEventChatbot';
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage } from './ChatMessage';
import { LeadCaptureForm } from './LeadCaptureForm';

interface EventChatbotProps {
  eventId?: string;
  eventName?: string;
}

export function EventChatbot({ eventId: eventIdProp, eventName }: EventChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Auto-detect event id from the current URL when not explicitly provided.
  // Supports /admin/events/:uuid (admin editor) and public /events/:category/:slug pages.
  const adminRouteEventId = useMemo(() => {
    const uuidRe = /\/admin\/events\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
    const m = location.pathname.match(uuidRe);
    return m?.[1];
  }, [location.pathname]);

  const publicRouteTarget = useMemo(() => {
    if (eventIdProp || adminRouteEventId) return null;
    const match = location.pathname.match(/\/events\/(festivals|academies|workations|retreats|awards)\/([^/?#]+)/i);
    if (!match) return null;
    const tableMap: Record<string, string> = {
      festivals: 'festivals',
      academies: 'academies',
      workations: 'workations',
      retreats: 'retreats',
      awards: 'award_editions',
    };
    return { table: tableMap[match[1].toLowerCase()], slug: decodeURIComponent(match[2]) };
  }, [adminRouteEventId, eventIdProp, location.pathname]);

  const { data: publicRouteEvent } = useQuery({
    queryKey: ['chatbot-route-event', publicRouteTarget?.table, publicRouteTarget?.slug],
    enabled: !!publicRouteTarget?.table && !!publicRouteTarget?.slug,
    queryFn: async () => {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(publicRouteTarget!.slug);
      const { data, error } = await supabase
        .from(publicRouteTarget!.table as any)
        .select('id, name')
        .eq(isUuid ? 'id' : 'slug', publicRouteTarget!.slug)
        .maybeSingle();

      if (error) {
        console.warn('Could not resolve event for chatbot route:', error);
        return null;
      }
      return data as unknown as { id: string; name?: string } | null;
    },
  });

  const eventId = eventIdProp || adminRouteEventId || publicRouteEvent?.id;
  const resolvedEventName = eventName || publicRouteEvent?.name;
  const routeContext = useMemo(() => ({
    table: publicRouteTarget?.table,
    slug: publicRouteTarget?.slug,
    pathname: location.pathname,
  }), [location.pathname, publicRouteTarget?.slug, publicRouteTarget?.table]);

  const {
    messages,
    isLoading,
    showLeadForm,
    setShowLeadForm,
    sendMessage,
    submitLead,
    clearMessages,
    isAdmin,
  } = useEventChatbot({ eventId, eventName: resolvedEventName, routeContext });

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Quick action buttons for empty state
  const quickActions = [
    { label: '📅 When is the next event?', message: 'When is the next Swiss Nomad Fest?' },
    { label: '🏨 What rooms are available?', message: 'What kind of accommodation options do you have?' },
    { label: '💰 How much does it cost?', message: 'What are the prices and packages available?' },
    { label: "✨ What's included?", message: "What's included in the event?" },
  ];

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg hover:scale-105 transition-transform"
        size="icon"
      >
        <MessageCircle className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <div
      className={cn(
        'fixed right-4 z-50 bg-background border rounded-xl shadow-2xl flex flex-col transition-all duration-300',
        isMinimized
          ? 'bottom-4 w-72 h-14'
          : 'bottom-4 w-[360px] h-[520px] max-h-[80vh]',
        'sm:w-[400px]'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-primary text-primary-foreground rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <MessageCircle className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Nomad Fest Assistant</h3>
            {!isMinimized && (
              <p className="text-xs opacity-80">Ask me anything! 🏔️</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isMinimized && messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={clearMessages}
              title="Clear chat"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Area */}
          <ScrollArea className="flex-1 p-3" ref={scrollRef}>
            <div className="space-y-3">
              {messages.length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-4xl mb-3">👋</div>
                  <h4 className="font-medium mb-2">Welcome to Nomad Fest!</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    I'm here to help you learn about our events and find your perfect adventure.
                  </p>
                  <div className="space-y-2">
                    {quickActions.map((action, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left h-auto py-2 px-3"
                        onClick={() => sendMessage(action.message)}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message, idx) => (
                  <ChatMessage
                    key={message.id}
                    role={message.role}
                    content={message.content}
                    isStreaming={isLoading && idx === messages.length - 1 && message.role === 'assistant'}
                    showActions={isAdmin}
                    assistantName="Nomad Fest Assistant"
                  />
                ))
              )}

              {/* Lead Capture Form */}
              {showLeadForm && (
                <LeadCaptureForm
                  onSubmit={submitLead}
                  onClose={() => setShowLeadForm(false)}
                  eventName={eventName}
                />
              )}
            </div>
          </ScrollArea>

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-3 border-t">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                type="submit"
                size="icon"
                disabled={isLoading || !inputValue.trim()}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
